<template>
  <select :name="name" :id="name" :placeholder="placeholder" :disabled="disabled"></select>
</template>

<script>
import { isEqual, find, each, isNaN, toNumber, cloneDeep, filter } from 'lodash'
import 'selectize'
import './plugins/auto-direction'
import './plugins/disable-delete'
import './plugins/infinite-scroll'
import $ from 'jquery'

export default {
  props: {
    name: {
      required: true,
      type: String
    },
    options: {
      required: true,
      type: Array
    },
    groups: {
      required: false,
      type: Array
    },
    value: {
      required: false,
      default: null
    },
    placeholder: {
      required: false,
      default: ' '
    },
    allowNewOptions: {
      required: false,
      type: Boolean,
      default: false
    },
    onNewOption: {
      required: false,
      type: Function
    },
    searchable: {
      required: false,
      type: Boolean,
      default: true
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false
    },
    renderOption: {
      required: false,
      type: Function
    },
    renderItem: {
      required: false,
      type: Function
    },
    fetchResults: {
      required: false,
      type: Function
    },
    searchField: {
      required: false,
      type: String,
      default: 'text'
    },
    disableDelete: {
      required: false,
      type: Boolean,
      default: false
    },
    preLoad: {
      required: false,
      type: Boolean,
      default: false
    },
    dropdownParent: {
      required: false,
      default: null
    },
    itemAdded: {
      required: false,
      type: Function
    },
    score: {
      required: false,
      type: Function
    },
    fetchMoreResults: {
      required: false,
      type: Function
    }
  },

  data () {
    return {
      element: null,
      selectize: null,
      first: true
    }
  },

  mounted () {
    this.element = $(this.$el)

    this.bind()
  },

  beforeDestroy () {
    this.selectize.destroy()
  },

  watch: {
    /**
      * Watch the disabled prop for changes and refresh the picker.
      *
      * @return void
      */
    disabled (disabled) {
      this.toggleDisabled(disabled)
    },

    /**
      * Watch the options for changes and refresh the picker.
      *
      * @return void
      */
    options: {
      handler (newOptions, oldOptions) {
        if (isEqual(newOptions, oldOptions)) {
          return
        }
        if (this.selectize) {
          let value = this.value

          this.selectize.clearOptions()

          each(this.groups, group => {
            this.selectize.addOptionGroup(group.id, group)
          })

          this.selectize.addOption(cloneDeep(newOptions))
          this.selectize.refreshOptions(false)

          if (!this.selectize.isOpen) {
            this.selectize.setValue(value, false)
          }
        }
      },
      deep: true
    },

    /**
      * Watch the parent v-model for any changes any have them reflected on the
      * select picker. Allows programatic selection.
      *
      * @param mixed value
      *
      * @return void
      */
    value (value) {
      this.selectize.$dropdown.find('.selected').not('.active').removeClass('selected')

      this.selectize.setValue(value, true)
    }
  },

  methods: {
    /**
      * Bind the selectize element.
      *
      * @return void
      */
    bind () {
      let plugins = ['auto_direction', 'disable_delete', 'remove_button']
      if (this.fetchMoreResults) {
        plugins.push('infinite_scroll')
      }

      this.selectize = this.element.selectize({
        plugins: plugins,
        delimiter: ',',
        dropdownDirection: 'auto',
        dropdownParent: this.dropdownParent,
        optgroups: cloneDeep(this.groups),
        options: cloneDeep(this.options),
        optgroupField: 'group',
        optgroupLabelField: 'text',
        optgroupValueField: 'id',
        labelField: 'text',
        valueField: 'id',
        highlight: false,
        disableDelete: this.disableDelete,
        create: this.onNewOption === undefined ? false : (input, callback) => {
          if (this.onNewOption) {
            this.onNewOption(input).then(callback)
          }
        },
        createFilter: input => {
          return find(this.options, option => option.text.toLowerCase() === input.toLowerCase()) === undefined
        },
        onType: (string) => {
          if (!string) {
            this.selectize.$dropdown_content.removeHighlight()
          }
        },
        onDelete: () => {
          setTimeout(() => {
            this.selectize.$dropdown_content.removeHighlight()
          }, 0)
        },
        onChange: this.handleChanged,
        onItemAdd: this.itemAdded === undefined ? null : (value) => {
          let option = find(this.options, (option) => String(option.id) === String(value))

          if (option) {
            this.itemAdded(option)
          }
        },
        onDropdownClose: dropdown => {
          $(dropdown).find('.selected').not('.active').removeClass('selected')
        },
        load: this.fetchResults || null,
        loadMore: this.fetchMoreResults || null,
        preload: this.preLoad,
        persist: true,
        render: this.renderOption || this.renderItem ? {
          option: this.renderOption || ((item, escape) => {
            return `<div class="option">${item.text}</div>`
          }),
          item: this.renderItem || ((data, escape) => {
            return `<div class="item">${data[this.selectize.settings.labelField]}</div>`
          })
        } : null,
        score: (search) => {
          let selectizeScore = this.selectize.getScoreFunction(search)
          let vm = this

          let score = function (item) {
            if (item.id === null || item.id === undefined || item.id === '') {
              return 0
            }

            return selectizeScore(item)
          }

          if (this.score) {
            return function (item) {
              return vm.score(item, score)
            }
          }

          return score
        }
      })[0].selectize

      this.selectize.setValue(this.value, true)

      this.toggleDisabled(this.disabled)
    },

    /**
      * Toggle disabled.
      *
      * @param boolean disabled
      *
      * @return void
      */
    toggleDisabled (disabled) {
      if (disabled) {
        this.selectize.disable()
      } else {
        this.selectize.enable()
      }

      this.selectize.setValue(this.value, true)
    },

    /**
      * Clears the selected input.
      *
      * @return void
      */
    clear () {
      this.selectize.clear()

      this.$emit('input', null)
    },

    /**
      * Handle the changed event, emits the input value up to the parent component.
      *
      * @return void
      */
    handleChanged (value) {
      this.selectize.$dropdown_content.removeHighlight()

      this.$emit('input', this.shouldBeNumber(value))
    },

    /**
      * Converts value to a number if it should be a number.
      *
      * For strict type comparisons.
      *
      * @param mixed value
      *
      * @return mixed
      */
    shouldBeNumber (value) {
      if (value === null || value === '') {
        return null
      } else if (typeof value === 'object') {
        return value
      }

      let number = toNumber(value)

      if (isNaN(number)) {
        return value
      }

      return number
    },

    /**
      * Determine if value is out of bounds of options.
      *
      * @param mixed value
      * @param array options
      *
      * @return boolean
      */
    isValueOutOfBounds (value, options) {
      return filter(options, option => option.id === value).length === 0
    }
  }
}
</script>
